import { startCase } from "lodash"
import { CCUtils, prettyJson } from "../../../../common/CCUtils"
import { log } from "../../../../common/Logging"
import { Align, FontType } from "../../../../common/TextUtils"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { ButtonContainer, Buttons, ButtonType } from "../../../generic-sprites/Buttons"
import { DebugPanel } from "../../../generic-sprites/DebugPanel"
import { GenericAssets } from "../../../generic-sprites/GenericAssets"
import { RadioGroup } from "../../../generic-sprites/RadioButtons"
import { Rectangle } from "../../../generic-sprites/Shape"
import { Audio } from "../../../utils/Audio"
import { PhaserText } from "../../../utils/PhaserText"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { Transitions } from "../../transitions/Transitions"
import { MakeVaperPartsStore } from "../MakeVaperPartsStore"
import { MakeVaperImages } from "../MakeYourOwnVaperAssets"
import { MakeVaperPt1Audio } from "../part-1/MakeYourOwnVaperPartOneAssets"
import {
    makeVaperClassmatesPartAudio, makeVaperMostClassmatesAudio, makeVaperPartTwoExitAudio, MakeVaperPt2Audio
} from "../part-2/MakeYourOwnVaperPartTwoAssets"
import { MakeVaperAgreement } from "../part-2/MakeYourOwnVaperPartTwoTypes"
import { MakeVaperSpudSprite } from "../sprites/MakeVaperSpudSprite"
import { MakeVaperContentKind, MakeVaperScreenContent } from "../types/MakeVaperScreenContentTypes"
import {
    MakeVaperScreenType, MakeVaperSpudPosition, MakeVaperSpudSize, MakeVaperTitleTextPosition, MakeVaperWhiteBoxPosition
} from "../types/MakeVaperScreenValueTypes"
import { MakeVaperActivityScreens, MakeVaperChoices, MakeVaperPart } from "../types/MakeYourOwnVaperTypes"

export class MakeVaperScreenBuilder {
    private static readonly contentX = Scale.x(0.09)
    private static readonly wordWrapWith = 0.4

    spud: MakeVaperSpudSprite
    currentButton: ButtonContainer

    private currentScreen: MakeVaperActivityScreens
    private currentScreenIndex = 0
    private radiosGroup: Array<RadioGroup>
    private reviewOptions = false

    private titleText: Phaser.Text
    private currentScreenGroup: Phaser.Group
    private readonly backgroundGroup: Phaser.Group
    private readonly spudGroup: Phaser.Group

    private rightBox: Rectangle
    private leftBox: Rectangle
    private leftCompare: Rectangle
    private rightCompare: Rectangle

    private compareSpud: MakeVaperSpudSprite

    private debugPanel: DebugPanel

    private constructor(
        private game: Phaser.Game,
        private screens: Array<MakeVaperActivityScreens>,
        private state: ActivityKey
    ) {
        this.backgroundGroup = game.add.group()
        this.currentScreenGroup = game.add.group()
        this.spudGroup = game.add.group()

        Sprites.add(this.game, MakeVaperImages.BACKGROUND, {group: this.backgroundGroup})
        this.spud = new MakeVaperSpudSprite(this.game, this.spudGroup, MakeVaperSpudPosition.RIGHT)
        this.createWhiteBoxes()
        this.debugPanel = new DebugPanel(game)
        // This is unfriendly, by which I mean everything blows up and it's hard to fix
        // this.debugPanel.setSkip(() => {
        //     this.startNextScreen()
        // }, false)
        this.startNextScreen()

    }

    static create = (game: Phaser.Game, state: ActivityKey, screens: Array<MakeVaperActivityScreens>) => new MakeVaperScreenBuilder(game, screens, state)

    static createOptionSprite = (
        game: Phaser.Game,
        group: Phaser.Group,
        asset: string,
        x: number,
        y: number,
        visible = false
    ): Phaser.Sprite => {
        const sprite = Sprites.add(game, asset, {
            debug: CCUtils.debug,
            scale: 0.9,
            group,
            x,
            y
        })
        sprite.visible = visible

        return sprite
    }

    moveSpud = (x: number, y: number) => {
        this.spud.moveSpud(x, y)
    }

    showEcig = (visible: boolean) => this.spud.viewEcig = visible

    forEachVaperPart = (f: (sprite: Phaser.Sprite) => void) => this.spud.vaperParts.forEach(f)

    private startNextScreen = () => {
        if (this.currentScreenIndex < this.screens.length) {
            // Set the new screen index, and update the current screen with the index
            this.currentScreen = this.screens[this.currentScreenIndex]

            // If this is not the first screen, destroy the current group before drawing the next screen
            if (this.currentScreenIndex > 0) {
                if (this.compareSpud) {
                    this.compareSpud.destroyTitle()
                }
                this.currentScreenGroup.destroy(true)
                this.currentScreenGroup = this.game.add.group()
            }

            this.drawCurrentScreen()
        }

        if (this.currentScreenIndex >= this.screens.length) {
            Transitions.startOutro(this.game, this.state)
        }
        this.currentScreenIndex++
    }

    /**
     * Function that is called whenever screen index is incremented. It will draw all the elements from the currentScreen object, and call
     * the appropriate function to handle the screen type.
     */
    private drawCurrentScreen = () => {
        let contentY: number

        // Show compare columns and sprite
        this.showCompareColumns(this.currentScreen.compareColumns)

        // Adds a large white graphic behind text and radio buttons
        this.showWhiteBox(this.currentScreen.whiteBox)

        if (this.currentScreen.titleImage) {
            this.createTitleImage()
            contentY = Scale.y(0.383)
        } else if (this.currentScreen.titleText) {
            const config = {debug: CCUtils.debug, group: this.currentScreenGroup}
            let text: Phaser.Text
            switch (this.currentScreen.titleText.position) {
                case MakeVaperTitleTextPosition.LEFT:
                    text = PhaserText.create(this.game, this.currentScreen.titleText.text, {
                        ...config,
                        x: Scale.x(0.11),
                        y: Scale.y(0.132),
                        wordWrapWidth: 0.4
                    })
                    break
                case MakeVaperTitleTextPosition.CENTER:
                    text = PhaserText.create(this.game, this.currentScreen.titleText.text, {...config, y: Scale.y(0.06)})
                    Sprites.centerInWorldXOnly(text)
                    break
                default:
                    throw new Error("Unimplemented title text position.")
            }
            this.titleText = text
            contentY = Scale.y(0.3)
        } else {
            contentY = Scale.y(0.196)
        }

        if (this.currentScreen.spudTitle) {
            this.createSpudTitle(this.currentScreen.centerContent)
        }

        const button = this.createScreenButton()

        if (this.currentScreen.centerContent) {
            button.centerInWorldX()
        }

        if (this.currentScreen.spudSize) {
            this.spud.scale = this.currentScreen.spudSize
        }

        this.spud.setSpudPosition(this.currentScreen.spudPosition ? this.currentScreen.spudPosition : MakeVaperSpudPosition.RIGHT)
        if (this.currentScreen.compareColumns) {
            const spudDelta = Scale.y(0.1)
            this.spud.moveSpud(this.spud.spud.x, this.spud.spud.y + spudDelta)
            this.compareSpud.moveSpud(this.compareSpud.spud.x, this.compareSpud.spud.y + spudDelta)
        }

        this.spud.showSpud(!this.currentScreen.hideSpud)

        if (this.currentScreen.showFullSpud) {
            this.spud.showAllSpudParts(this.currentScreen.showFullSpud)
        }

        switch (this.currentScreen.type) {
            case MakeVaperScreenType.TIMED:
                this.timedContentScreen(contentY, button)
                break
            case MakeVaperScreenType.OPTION:
                this.optionContentScreen(contentY, button)
                break
            case MakeVaperScreenType.STEPS:
                this.stepContentScreen(button)
                break
            case MakeVaperScreenType.AGREEMENT:
                this.agreementContentScreen(button)
                break
            case MakeVaperScreenType.EXIT:
                this.exitContentScreen(button)
                break
            default:
                throw new Error("Unimplemented screen type.")
        }
    }

    /**
     * Function that plays outro audio for Pt. 2 and displays both spuds side by side.
     * @param button
     */
    private exitContentScreen = (button: ButtonContainer) => {
        this.compareSpud.showAllSpudParts(MakeVaperSpudSprite.classVaperParts)
        this.formatExitScreen()
        Audio.add(this.game, makeVaperPartTwoExitAudio(MakeVaperPartsStore.instance.agreement), {onStop: () => button.enable(), stopAllAudio: false})
    }

    private formatAgreementScreen = () => {
        this.moveSpud(Scale.x(0.68), Scale.y(0.3))
        this.rightCompare.graphics.y -= Scale.y(0.1)
        this.leftCompare.graphics.y -= Scale.y(0.1)
        this.compareSpud.moveSpud(this.compareSpud.spud.x, this.compareSpud.spud.y - Scale.y(0.1))
    }

    private formatExitScreen = () => {
        this.moveSpud(this.spud.spud.x, this.spud.spud.y - Scale.y(0.1))
        this.compareSpud.moveSpud(this.compareSpud.spud.x, this.compareSpud.spud.y - Scale.y(0.1))
    }

    /**
     * Function that displays the comparison and user spud with radio button options to change their spud.
     * @param button
     */
    private agreementContentScreen = (button: ButtonContainer) => {
        const currentSpud = MakeVaperPartsStore.instance.spud
        this.compareSpud.showAllSpudParts(MakeVaperSpudSprite.classVaperParts)
        this.formatAgreementScreen()
        const agree = MakeVaperSpudSprite.checkAgreement(currentSpud)
        MakeVaperPartsStore.instance.agreement = agree

        const buttonFormat = (b: ButtonContainer, right = false) => {
            Sprites.centerSpriteInSprite(this.rightCompare.graphics, b.button)
            right ? b.button.x += Scale.x(0.1) : b.button.x -= Scale.x(0.1)
            b.button.y += (this.rightCompare.graphics.height / 2)
            b.center()
            b.disable()
        }
        const buttonFunction = () => {
            const newAgree = MakeVaperSpudSprite.checkAgreement(MakeVaperPartsStore.instance.spud)
            MakeVaperPartsStore.instance.agreement = newAgree
            const agreeAudio = this.game.add.audio(newAgree === MakeVaperAgreement.AGREE ? MakeVaperPt2Audio.NOW_AGREE : MakeVaperPt2Audio.STILL_DONT_AGREE)
            agreeAudio.onPlay.addOnce(() => button.disable())
            agreeAudio.onStop.addOnce(() => button.enable())
            agreeAudio.play()
        }
        const fontPx = 14
        const config = {group: this.currentScreenGroup, textConfig: {fontPx}}
        const changeButton = Buttons.create(this.game, "Change", buttonFunction, config)
        const dontChangeButton = Buttons.create(this.game, "Don't Change", buttonFunction, config)
        buttonFormat(changeButton)
        buttonFormat(dontChangeButton, true)

        const x = Scale.x(0.56)
        let y = Scale.y(0.58)
        const buttons = CCUtils.enumValues(MakeVaperPart).map((part: MakeVaperPart) => {
            const text = PhaserText.create(this.game, startCase(part), {debug: CCUtils.debug, group: this.currentScreenGroup, fontPx, x, y})
            const radios = new RadioGroup(
                this.game,
                x + Scale.x(0.18),
                y + Scale.y(0.01),
                [{text: "Yes"}, {text: "No"}],
                () => {
                    this.checkSelectedOption(radios.currentlySelected, changeButton, part)
                    dontChangeButton.disable()
                },
                false,
                true,
                fontPx,
                this.currentScreenGroup
            )
            const yesForPart = currentSpud[part]
            log.debug(`In spud radios yes for part ${part} is ${yesForPart} current spud: ${prettyJson(currentSpud)}`)
            radios.buttons[yesForPart ? 0 : 1].selected = true
            y += text.height + Scale.y(0.01)
            return radios
        })

        let audio: Phaser.Sound
        if (agree === MakeVaperAgreement.AGREE) {
            audio = this.game.add.audio(MakeVaperPt2Audio.ALREADY_AGREE).play()
            audio.onStop.addOnce(() => button.enable())
        } else {
            const keys = this.dontAgreeParts(currentSpud).map((part: MakeVaperPart) => makeVaperMostClassmatesAudio(part))
            const dontAgreeAudios = this.createDontAgreeAudios(keys, [])
            dontAgreeAudios.shift().play()
            audio = dontAgreeAudios.pop()
            audio.onStop.addOnce(() => buttons.forEach(radio => {
                radio.enable()
                dontChangeButton.enable()
            }))
        }
    }

    /**
     * Returns an array of MakeVaperParts that do not agree with the class' Mr. Spud parts.
     * @param spud
     */
    private dontAgreeParts = (spud: MakeVaperChoices) =>
        CCUtils.enumValues(MakeVaperPart).filter((part: MakeVaperPart) => MakeVaperSpudSprite.classVaperParts[part] !== spud[part])

    /**
     * Recursive function that takes in a list of audio keys, and chains the audios together in the array order. Returns an array of the created
     * Phaser.Sounds, with the appropriate ending audio appended.
     * @param keys
     * @param audios
     * @param prevAudio
     */
    private createDontAgreeAudios = (keys: Array<string>, audios: Array<Phaser.Sound>, prevAudio?: Phaser.Sound): Array<Phaser.Sound> => {
        if (keys.length === 0) {
            const endAudio = this.game.add.audio(MakeVaperPt2Audio.DONT_AGREE_CHANGE)
            prevAudio.onStop.addOnce(() => endAudio.play())
            audios.push(endAudio)
            return audios
        }
        const current = this.game.add.audio(keys.shift())
        audios.push(current)
        if (prevAudio) {
            prevAudio.onStop.addOnce(() => current.play())
        }
        return this.createDontAgreeAudios(keys, audios, current)
    }

    /**
     * Function that handles activity screens with multiple sub steps (i.e. Compare Spuds)
     * @param button
     */
    private stepContentScreen = (button: ButtonContainer) => {
        const steps = this.currentScreen.screenContent.length - 1
        let contentIndex = 0

        button.disable()
        const buttonOnClick = () => {
            if (contentIndex < steps) {
                button.disable()
                contentIndex += 1
                this.handleStepScreenContent(contentIndex, button)
            } else {
                this.startNextScreen()
            }
        }
        button.setOnClick(buttonOnClick)

        const buttonEnable = () => {
            button.enable()
            button.setOnClick(buttonOnClick)
        }

        if (this.currentScreen.screenAudio) {
            Audio.add(this.game, this.currentScreen.screenAudio, {play: true, onPlay: () => button.disable(), onStop: buttonEnable})
        } else {
            this.handleStepScreenContent(contentIndex, button)
        }
    }

    private handleStepScreenContent = (contentIndex: number, button: ButtonContainer) => {
        const content = this.currentScreen.screenContent[contentIndex]
        if (content.kind === MakeVaperContentKind.COMPARE) {
            this.spud.destroyTitle()
            this.compareSpud.destroyTitle()
            this.titleText.setText(`${startCase(content.part)}`, true)
            const subtitle = PhaserText.create(this.game, `Do you think kids who vape are ${startCase(content.part)}?`, {
                group: this.currentScreenGroup,
                fontPx: 18
            })
            Sprites.centerInWorldXOnly(subtitle)
            Sprites.centerInWorldXOnly(this.titleText)
            subtitle.y = this.titleText.y + this.titleText.height + Scale.y(0.001)
            button.setOnClickOnce(() => subtitle.destroy())
            const classmateAudio = this.game.add.audio(makeVaperClassmatesPartAudio(content.part)).play()
            const respAudio = this.game.add.audio(content.response ? MakeVaperPt2Audio.DONT_AGREE : MakeVaperPt2Audio.AGREE)
            const clickNext = this.game.add.audio(MakeVaperPt2Audio.CLICK_NEXT)
            classmateAudio.onStop.addOnce(() => respAudio.play())
            respAudio.onPlay.addOnce(() => this.compareSpud.setPartResponse(content.part, false))
            respAudio.onStop.addOnce(() => clickNext.play())
            clickNext.onStop.addOnce(button.enable)
        } else {
            throw new Error("Unimplemented screen content given to step.")
        }
    }

    private timedContentScreen = (y: number, button: ButtonContainer) => {
        const audio = this.game.add.audio(this.currentScreen.screenAudio)
        const timer = this.game.time.create(true)
        const spacing = this.currentScreen.titleImage ? Scale.y(0.02) : Scale.y(0.05)
        const textCreator = PhaserText.orderedTextList(this.game, {
            debug: CCUtils.debug,
            wordWrapWidth: MakeVaperScreenBuilder.wordWrapWith,
            x: MakeVaperScreenBuilder.contentX,
            group: this.currentScreenGroup,
            y
        }, spacing)
        this.currentScreen.screenContent.forEach((content: MakeVaperScreenContent) => {
            switch (content.kind) {
                case MakeVaperContentKind.LETTERED_TEXT:
                    timer.add(content.delay, () => textCreator(content.text))
                    break
                case MakeVaperContentKind.FUNCTION:
                    timer.add(content.delay, content.function)
                    break
                case MakeVaperContentKind.CENTER_TEXT:
                    const config = {align: Align.CENTER, wordWrapWidth: 0.4, group: this.currentScreenGroup}
                    const title = PhaserText.create(this.game, content.title, {...config, weight: "bold"})
                    const text = PhaserText.create(this.game, content.text, config)
                    Sprites.centerTextInSprite(this.backgroundGroup, title)
                    Sprites.centerTextInSprite(this.backgroundGroup, text)
                    title.y -= Scale.y(0.2)
                    break
                default:
                    log.error("Cannot accept this kind of content.")
            }
        })
        audio.onPlay.add(() => timer.start())
        if (!(this.currentScreen.enableOnAudioStop === false)) {
            audio.onStop.add(button.enable)
        }
        audio.play()
    }

    private optionContentScreen = (y: number, button: ButtonContainer) => {
        this.radiosGroup = []
        let currentRadio: RadioGroup
        let yValue = y
        let optionIndex = 0
        button.setOnClick(() => {
            this.spud.destroyTitle()
            if (optionIndex < this.currentScreen.screenContent.length - 1) {
                optionIndex += 1
                yValue += Scale.y(0.1)
                currentRadio.disable()
                currentRadio = this.displayCurrentOption(optionIndex, yValue, button)
                this.radiosGroup.push(currentRadio)
            } else {
                currentRadio.disable()
                const audio = this.game.add.audio(MakeVaperPt1Audio.REVIEW_VAPER)
                audio.onPlay.addOnce(button.disable)
                audio.onStop.addOnce(() => {
                    this.reviewOptions = true
                    this.radiosGroup.forEach(radio => radio.enable())
                    button.enable()
                    Buttons.undoSpriteButton(button.button)
                    Buttons.makeSpriteButton(button.button, () => {
                        this.spud.destroyTitle()
                        if (this.compareSpud) {
                            this.compareSpud.destroyTitle()
                        }
                        this.radiosGroup.forEach(radio => radio.destroy())
                        this.startNextScreen()
                    })
                })
                audio.play()
            }
        })
        currentRadio = this.displayCurrentOption(optionIndex, yValue, button)
        this.radiosGroup.push(currentRadio)
    }

    private displayCurrentOption(optionIndex: number, yValue: number, button: ButtonContainer): RadioGroup {
        const textColumn = Scale.x(0.11)
        const radioColumn = Scale.x(0.3)
        button.disable()

        const content = this.currentScreen.screenContent[optionIndex]
        if (content.kind === MakeVaperContentKind.OPTION) {
            const radioY = yValue + Scale.y(0.02)
            PhaserText.create(this.game, content.text, {
                debug: CCUtils.debug,
                group: this.currentScreenGroup,
                x: textColumn,
                y: yValue
            })
            const radios = new RadioGroup(
                this.game,
                radioColumn,
                radioY,
                [{text: "Yes"}, {text: "No"}],
                () => {
                    this.checkSelectedOption(radios.currentlySelected, button, content.part)
                    if (this.reviewOptions) {
                        if (this.radiosGroup.filter(radio => radio.currentlySelected === undefined).length === 0) {
                            button.enable()
                        } else {
                            button.disable()
                        }
                    }
                },
                false,
                true
            )
            const audio = this.game.add.audio(content.audio)
            audio.onStop.add(radios.enable)
            audio.play()
            return radios
        } else {
            throw new Error("Cannot accept this kind of content.")
        }
    }

    private checkSelectedOption = (
        option: string,
        button: ButtonContainer,
        part: MakeVaperPart
    ) => {
        this.spud.destroyTitle()
        switch (option) {
            case "Yes":
                this.spud.setPartResponse(part, true)
                MakeVaperPartsStore.instance.addPart(part, true)
                button.enable()
                break
            case "No":
                this.spud.setPartResponse(part, false)
                MakeVaperPartsStore.instance.addPart(part, false)
                button.enable()
                break
            default:
                button.disable()
        }
    }

    private createScreenButton = (content = "Next"): ButtonContainer => {
        const onClick = () => {
            this.startNextScreen()
            this.spud.destroyTitle()
        }
        const button = Buttons.create(this.game, content, onClick, {
            buttonType: ButtonType.SECONDARY,
            group: this.currentScreenGroup,
            x: Scale.x(0.255),
            y: Scale.y(0.94),
        })
        button.disable()
        this.currentButton = button

        return button
    }

    private createTitleImage = () => PhaserText.create(this.game, "MAKE YOUR OWN VAPER", {
        group: this.currentScreenGroup,
        x: Scale.x(0.065),
        y: Scale.y(0.144),
        fontPx: 40,
        type: FontType.SILLY_TITLE
    })

    private createWhiteBoxes = () => {
        const compareColWidth = Scale.x(0.4)
        const compareColHeight = Scale.y(0.725)
        const compareColY = Scale.y(0.2)
        const config = {
            lineColor: Phaser.Color.GRAY,
            lineWidth: 3,
            group: this.backgroundGroup,
            fillColor: Phaser.Color.WHITE,
            cornerRounding: 10
        }
        this.rightBox = new Rectangle(this.game, Scale.x(0.47), Scale.y(0.125), Scale.x(0.48), Scale.y(0.725), config)
        this.leftBox = new Rectangle(this.game, Scale.x(0.07), Scale.y(0.1), Scale.x(0.48), Scale.y(0.8), config)
        this.rightCompare = new Rectangle(this.game, Scale.x(0.53), compareColY, compareColWidth, compareColHeight, config)
        this.leftCompare = new Rectangle(this.game, Scale.x(0.07), compareColY, compareColWidth, compareColHeight, config)

        // Title for each compare box
        this.addCompareTitle(this.rightCompare, "Your Mr. Spud")
        this.addCompareTitle(this.leftCompare, "Your Classmates'")

        this.rightBox.graphics.visible = false
        this.leftBox.graphics.visible = false
        this.leftCompare.graphics.visible = false
        this.rightCompare.graphics.visible = false
    }

    private addCompareTitle = (rect: Rectangle, title: string) => {
        const widthPadding = 20 * Scale.scaleAmount()
        const heightPadding = 8 * Scale.scaleAmount()
        const box = Sprites.add(this.game, GenericAssets.TEXT_BOX, {group: this.backgroundGroup})
        const text = PhaserText.create(this.game, title, {fontPx: 16, group: this.backgroundGroup})
        box.anchor.set(0.5, 0.5)
        text.anchor.set(0.5, 0.5)
        box.width = text.width + widthPadding * 2
        box.height = text.height + heightPadding * 2
        rect.graphics.addChild(box)
        box.x += rect.graphics.width / 2
        box.addChild(text)
    }

    private showCompareColumns = (show: boolean) => {
        this.leftCompare.graphics.visible = !!show
        this.rightCompare.graphics.visible = !!show
        if (show) {
            this.compareSpud = new MakeVaperSpudSprite(this.game, this.currentScreenGroup, MakeVaperSpudPosition.LEFT)
            this.compareSpud.scale = this.currentScreen.spudSize ? this.currentScreen.compareSpudSize : MakeVaperSpudSize.LARGE
        } else {
            if (this.compareSpud) {
                this.compareSpud.spud.destroy(true)
            }
        }
    }

    private showWhiteBox = (position: MakeVaperWhiteBoxPosition) => {
        switch (position) {
            case MakeVaperWhiteBoxPosition.RIGHT:
                this.rightBox.graphics.visible = true
                this.leftBox.graphics.visible = false
                break
            case MakeVaperWhiteBoxPosition.LEFT:
                this.rightBox.graphics.visible = false
                this.leftBox.graphics.visible = true
                break
            default:
                this.rightBox.graphics.visible = false
                this.leftBox.graphics.visible = false
        }
    }

    private createSpudTitle = (center?: boolean) => {
        const titleBox = this.game.add.graphics()
        this.currentScreenGroup.add(titleBox)
        titleBox.beginFill(Phaser.Color.WHITE)
        titleBox.lineStyle(1, Phaser.Color.BLACK, 1)
        titleBox.drawRect(0, 0, Scale.x(0.3), Scale.y(0.1))
        titleBox.endFill()
        titleBox.visible = false

        const boxSprite = this.game.add.sprite(Scale.x(0.615), 0, titleBox.generateTexture())
        this.currentScreenGroup.add(boxSprite)
        if (CCUtils.debug) {
            Sprites.enableDebug(boxSprite)
        }
        const text = PhaserText.create(this.game, "Mr. Spud", {
            debug: CCUtils.debug,
            group: this.currentScreenGroup
        })

        if (center) {
            Sprites.centerInWorldXOnly(boxSprite)
        }
        Sprites.centerTextInSprite(boxSprite, text)
    }
}

